import { CalendarIcon } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { DateRange } from "react-day-picker";
import { format } from "date-fns";

export type DateFilterValue =
  | "today"
  | "yesterday"
  | "last7"
  | "last30"
  | "thisMonth"
  | "lastMonth"
  | "custom";

const dateFilterOptions: { value: DateFilterValue; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7", label: "Last 7 days" },
  { value: "last30", label: "Last 30 days" },
  { value: "thisMonth", label: "This month" },
  { value: "lastMonth", label: "Last month" },
  { value: "custom", label: "Custom" },
];

interface DateFilterProps {
  value: DateFilterValue;
  onChange: (value: DateFilterValue) => void;
  rangeLabel?: string;
  customRange?: DateRange;
  onCustomRangeChange?: (range: DateRange | undefined) => void;
}

export function DateFilter({
  value,
  onChange,
  rangeLabel,
  customRange,
  onCustomRangeChange,
}: DateFilterProps) {
  return (
    <div className="flex items-center gap-2">
      <Select
        value={value}
        onValueChange={(v) => onChange(v as DateFilterValue)}
      >
        <SelectTrigger className="h-9 w-[130px] border border-gray-200 bg-white text-xs font-medium text-gray-700 shadow-none">
          <SelectValue />
        </SelectTrigger>
        <SelectContent align="end" className="rounded-lg">
          {dateFilterOptions.map((opt) => (
            <SelectItem key={opt.value} value={opt.value} className="text-xs">
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {value === "custom" && onCustomRangeChange && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="h-9 border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 shadow-none"
            >
              <CalendarIcon className="mr-1.5 h-3.5 w-3.5 text-gray-500" />
              {rangeLabel || "Pick range"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto rounded-lg p-0" align="end">
            <Calendar
              mode="range"
              selected={customRange}
              onSelect={onCustomRangeChange}
              numberOfMonths={2}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      )}

      {value !== "custom" && rangeLabel && (
        <span className="text-xs text-gray-400">{rangeLabel}</span>
      )}
    </div>
  );
}
